/**
 * Scale-degree sequences — the platform-portable representation of a
 * melody as a list of functional roles in a key, with optional chromatic
 * alterations and octave deltas.
 *
 * Used by the Phase 2A Block D melodic-dictation exercise type, by
 * `@music-app/input-midi`'s `gradeMidiSequence`, and by the
 * `<DictationInput>` UI. Pure data; no audio, no DOM.
 *
 * Why this lives in `theory-engine` rather than alongside the dictation
 * grader: it's a music-theory primitive (a melody as functional role,
 * the same idea movable-do solfège captures) and we want every package
 * that needs to *talk about* a melody-by-degree to share one shape.
 * Keeping it here also frees `input-midi` from re-declaring the same
 * structure inline.
 */

/** A single scale-degree note — the building block of a melodic sequence. */
export interface ScaleDegree {
  /** 1..7. Diatonic scale degree in the surrounding key. */
  degree: number;
  /**
   * Chromatic alteration of the diatonic degree. Absent (or `undefined`)
   * means the natural diatonic spelling. Raised (`♯`) is preferred over
   * the enharmonic lowered spelling for the same pitch class
   * (e.g. ♯4 over ♭5).
   */
  alteration?: "♯" | "♭";
  /**
   * Octave delta from the reference octave (the octave the generator's
   * audio plan plays the target melody in — typically the octave above
   * the established tonic). 0 means the same octave as the heard melody;
   * +1 is one octave higher; -1 one lower.
   *
   * Optional and `0` by default. Octave-agnostic graders ignore this
   * field; strict-octave graders compare it.
   */
  octaveOffset?: number;
}

/** A melody as a sequence of scale degrees. Read-only — graders do not mutate. */
export type ScaleDegreeSequence = ReadonlyArray<ScaleDegree>;

/* ─── Movable-do mapping tables ──────────────────────────────────────── */

/**
 * Semitone offset (mod 12) → diatonic degree, for major. The same table
 * works for any movable-do major-mode key: subtract the tonic's MIDI from
 * the played note, take mod 12, look up the degree.
 */
const MAJOR_DEGREE_BY_SEMITONE: Record<number, number> = {
  0: 1, 2: 2, 4: 3, 5: 4, 7: 5, 9: 6, 11: 7,
};

/**
 * Minor semitone → degree. Degree numbers are mode-agnostic (same offsets
 * whether do-based or la-based); only the solfège syllable names differ.
 * Matches the convention in `@music-app/exercise-generator`'s scale-degree generator.
 */
const MINOR_DEGREE_BY_SEMITONE: Record<number, number> = {
  0: 1, 2: 2, 3: 3, 5: 4, 7: 5, 8: 6, 10: 7,
};

export interface MidiToScaleDegreeOptions {
  /**
   * When false (default), only diatonic notes map to a degree; chromatic
   * notes return `null`. When true, chromatic notes are spelled with the
   * `alteration` field, raised over lowered (♯4 wins over ♭5).
   *
   * Diatonic mapping is stable: a chromatic input that happens to hit a
   * diatonic semitone (e.g. `♯3` enharmonic with `4`) gets the diatonic
   * spelling — the user heard "fa", not "mi sharp".
   */
  allowChromatic?: boolean;
  /**
   * Reference tonic MIDI for octave-offset computation. When provided
   * AND the result is non-null, the returned `ScaleDegree.octaveOffset`
   * is set to the integer octave delta from `referenceTonicMidi` to the
   * input pitch (rounded toward the input's octave). Without this, the
   * result has no `octaveOffset` and is octave-agnostic.
   *
   * Typical caller: piano-mode dictation input. The reference tonic is
   * the octave the generator chose for the target melody, so
   * `octaveOffset === 0` means "same octave the user heard."
   */
  referenceTonicMidi?: number;
}

/**
 * Map a MIDI note → scale degree (1..7) given a tonic and mode.
 *
 * Octave-agnostic by default — only the pitch class matters. Returns
 * `null` for chromatic notes unless `allowChromatic: true`. When
 * `referenceTonicMidi` is provided, the result includes an
 * `octaveOffset` so callers that care about register (e.g. strict
 * dictation grading) can compare octaves too.
 */
export function midiToScaleDegree(
  midi: number,
  tonicMidi: number,
  mode: "major" | "minor",
  opts: MidiToScaleDegreeOptions = {},
): ScaleDegree | null {
  const rounded = Math.round(midi);
  const tonicRounded = Math.round(tonicMidi);
  const offset = ((rounded - tonicRounded) % 12 + 12) % 12;
  const table = mode === "major" ? MAJOR_DEGREE_BY_SEMITONE : MINOR_DEGREE_BY_SEMITONE;

  let result: ScaleDegree | null = null;
  const direct = table[offset];
  if (direct !== undefined) {
    result = { degree: direct };
  } else if (opts.allowChromatic) {
    // Prefer raised spelling: a half-step below a diatonic degree → ♯ of
    // the lower degree (♯4 over ♭5, ♯1 over ♭2, etc.). Fall back to the
    // lowered spelling only when raised doesn't land on a diatonic degree.
    const raisedFrom = table[(offset - 1 + 12) % 12];
    if (raisedFrom !== undefined) {
      result = { degree: raisedFrom, alteration: "♯" };
    } else {
      const loweredFrom = table[(offset + 1) % 12];
      if (loweredFrom !== undefined) {
        result = { degree: loweredFrom, alteration: "♭" };
      }
    }
  }

  if (!result) return null;

  if (opts.referenceTonicMidi !== undefined) {
    const refRounded = Math.round(opts.referenceTonicMidi);
    // Floor-division of (input - reference) by 12 — gives the octave
    // delta from the reference octave to the input. e.g. ref=72 input=60
    // → -1; ref=72 input=84 → +1; ref=72 input=72 → 0.
    const octaveOffset = Math.floor((rounded - refRounded) / 12);
    if (octaveOffset !== 0) {
      return { ...result, octaveOffset };
    }
    return { ...result, octaveOffset: 0 };
  }

  return result;
}

/* ─── Inverse: scale degree → MIDI ───────────────────────────────────── */

/**
 * Map a scale degree back to a MIDI note in a given key. The inverse of
 * {@link midiToScaleDegree}. Used by the dictation generator's audio
 * plan to render an `expectedSequence` and by debug UIs that want to
 * play back what the user submitted.
 *
 * `octaveOffset` shifts the result by whole octaves from the reference
 * (the octave above `tonicMidi`). `alteration` shifts by ±1 semitone.
 */
export function scaleDegreeToMidi(
  d: ScaleDegree,
  tonicMidi: number,
  mode: "major" | "minor",
): number {
  const table = mode === "major" ? MAJOR_OFFSETS : MINOR_OFFSETS;
  const baseOffset = table[((d.degree - 1) % 7 + 7) % 7]!;
  const altShift = d.alteration === "♯" ? 1 : d.alteration === "♭" ? -1 : 0;
  const octave = d.octaveOffset ?? 0;
  return tonicMidi + 12 + baseOffset + altShift + octave * 12;
}

/** Diatonic degree (1..7) → semitones above tonic, major. */
const MAJOR_OFFSETS = [0, 2, 4, 5, 7, 9, 11] as const;

/** Diatonic degree (1..7) → semitones above tonic, la-based minor. */
const MINOR_OFFSETS = [0, 2, 3, 5, 7, 8, 10] as const;
